import { Component, DestroyRef, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { API, InventoryClientDTO } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

export enum CreateClientConnectionStatus {
  TOKEN,
  CHECK,
  REQUEST_SEND,
}

export const ConnectClientModalID = 'connectClientModal';

@Component({
  selector: 'sb-page-create-client-connection',
  templateUrl: './create-client-connection.component.html',
  styleUrls: ['./create-client-connection.component.scss'],
  standalone: false,
})
export class CreateClientConnectionComponent implements OnInit {
  CreateClientConnectionStatus = CreateClientConnectionStatus;

  @Output() private done: EventEmitter<boolean>;

  public form: FormGroup;
  public errorMessage: string;
  public client: InventoryClientDTO;
  public openedWithDefaultToken: boolean;
  public status: CreateClientConnectionStatus;

  get Token(): FormControl {
    return this.form.get('Token') as FormControl;
  }

  constructor(
    private api: API,
    private modalService: ModalService,
    private errorService: ErrorService,
    private destroyRef: DestroyRef
  ) {
    this.done = new EventEmitter<boolean>();
    this.status = CreateClientConnectionStatus.TOKEN;
    this.form = new FormGroup({
      Token: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    const payload = this.modalService.getPayload(ConnectClientModalID);
    this.openedWithDefaultToken = false;
    if (payload?.token) {
      this.openedWithDefaultToken = true;
      this.Token.setValue(payload.token);
      this._checkToken();
    }
  }

  private _checkToken() {
    if (this.form.valid) {
      this.api
        .shared_ClientConnection_findClientByInvitationCode(this.Token.value)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: client => {
            if (client) {
              this.client = client;
              this.status = CreateClientConnectionStatus.CHECK;
            } else {
              this.Token.setErrors({ notFoundError: true });
            }
          },
          error: error => {
            if (error.status === 404) {
              this.Token.setErrors({ notFoundError: true });
            } else {
              this.errorService.errorHandler(error);
            }
          },
        });
    }
  }

  public next(): void {
    switch (this.status) {
      case CreateClientConnectionStatus.TOKEN:
        this._checkToken();
        break;

      case CreateClientConnectionStatus.CHECK:
        if (this.client) {
          this.errorMessage = null;
          this.api
            .shared_ClientConnection_requestClientConnectionEstablishment(this.Token.value)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              take(1),
              finalize(() => {
                this.status = CreateClientConnectionStatus.REQUEST_SEND;
              })
            )
            .subscribe({
              next: () => {},
              error: error => {
                this.errorMessage = error.error.message;
              },
            });
        }
        break;

      case CreateClientConnectionStatus.REQUEST_SEND:
        this.close();
        break;
    }
  }

  public previous(): void {
    if (this.status === CreateClientConnectionStatus.CHECK) {
      this.status = CreateClientConnectionStatus.TOKEN;
    }
  }

  public get clientAddress(): string {
    if (this.client?.address?.street && this.client?.address?.zip && this.client?.address?.city) {
      return this.client.address.street + ', ' + this.client.address.zip + ' ' + this.client.address.city;
    }
    return undefined;
  }

  private close(): void {
    this.done.emit(!this.errorMessage);
  }
}
